import { Component } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { Router } from '@angular/router';
import { interval, Subscription } from 'rxjs';
import { mergeMap, takeWhile } from 'rxjs/operators';
import {
  CreateDailyProductImpressionCsvReportRequest,
  CreateProductImpressionCsvReportRequest,
  IsReportReadyRequest,
  IsReportReadyResponse,
  ListProductImpressionsRequest,
  ProductImpressionReportType,
  ProductImpressionView,
  ProductName,
  SituAnalyticsWebApiClient
} from 'situanalytics-webapi-client';
import { GetProductRequest, GetProductResponse, SituViewWebApiClient } from 'situview-webapi-client';
import { DownloadReportDialogComponent } from 'src/app/components/download-report-dialog/download-report-dialog.component';
import { AuthService, BreadcrumbService, ConfigService, IProductNameConfiguration, ProductNameService, StateService } from 'src/app/services';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';


@Component({
  selector: 'view-product-impressions',
  templateUrl: './product-impressions.component.html',
  styleUrls: ['./product-impressions.component.less'],
})
export class ProductImpressionsComponent extends SituBaseComponent {

  private readonly defaultColumns = ['manufacturer', 'productName', 'action', 'builder', 'home', 'masterFile', 'room', 'view'];
  private readonly perManufacturerColumns = ['manufacturer', 'totalImpressions', 'billableImpressions', 'manufacturerDetail'];
  private readonly perProductColumns = ['manufacturer', 'productName', 'totalImpressions'];

  private _loading = false;
  public get loading(): boolean {
    return this._loading;
  }

  public set loading(val: boolean) {
    this._loading = val;
    this.breadcrumbService.setIsLoading(val);
  }

  pageNumber = 1;
  pageSize = 25;
  totalItems = 0;
  productImpressions: ProductImpressionView[] = [];
  manufacturers: string[] = [];
  products: IProductNameConfiguration[] | undefined;
  reportId = '';
  isReportReady = false;
  reportErrorCount = 0;
  reportPollCount = 0;

  displayedColumns: string[] = this.defaultColumns;

  dailyDate: Date;
  dateFrom: Date;
  dateTo: Date;
  reportType: ProductImpressionReportType = ProductImpressionReportType.Default;
  manufacturer: string = '';
  product: IProductNameConfiguration | undefined = '' as any;

  pollForReportStatusSub: Subscription | undefined;

  constructor(
    authService: AuthService,
    stateService: StateService,
    private situAnalyticsClient: SituAnalyticsWebApiClient,
    private situViewClient: SituViewWebApiClient,
    public dialog: MatDialog,
    private productNameService: ProductNameService,
    private breadcrumbService: BreadcrumbService,
    private configService: ConfigService,
    private router: Router) {
    super(authService, stateService);
    this.situLinkBaseUrl = this.configService.config.situLinkBaseUrl;
    this.situPortalBaseUrl = this.configService.config.situPortalBaseUrl;

    const now = new Date();
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);

    this.dateFrom = yesterday;
    this.dateTo = now;
    this.dailyDate = now;
    this.products = this.productNameService.productNameConfigurations;

    //this.loadData();

    // this.situViewClient.listManufacturerProducts(new ListManufacturerProductsRequest({ correlationId: "7bda659c-9ad5-403c-9f86-ba6e289d1a8f" }))
    //   .then(response => {
    //     if (response.success && response.manufacturerProducts) {
    //       this.manufacturers = [...new Set(response.manufacturerProducts.map(x => x.manufacturer ?? ''))];
    //     }
    //     this.loading = false;
    //   }, error => {
    //     this.loading = false;
    //   });
  }

  protected onSelectedCompanyChange(): void {
    this.loadData();
  }

  situLinkBaseUrl: string | undefined;
  situPortalBaseUrl: string | undefined;
  openSituLinkInNewWindow(event: MouseEvent, artifactId: string, isFacade: boolean = false) {
    event.stopPropagation();
    const url = this.getSituLinkUrl(artifactId, isFacade);
    window.open(url, '_blank');
  }

  getSituLinkUrl(artifactId: string, isFacade: boolean = false): string {
    if (!artifactId) {
      return "";
    }

    const baseUrl = this.situLinkBaseUrl
      ? `https://${isFacade ? 'facade' : 'view'}.${this.situLinkBaseUrl}`
      : this.situPortalBaseUrl!;

    const url = new URL(baseUrl);
    url.pathname = artifactId;

    return url.toString();
  }

  openProductInNewWindow(event: MouseEvent, item: ProductImpressionView) {
    event.stopPropagation();

    if (!item?.productId) {
      return;
    }

    let request = new GetProductRequest();
    request.productId = item.productId;

    this.situViewClient.getProduct(request)
      .then((getProductResponse: GetProductResponse) => {
        if (getProductResponse.success && getProductResponse.product) {
          this.router.navigate(['/products/product', item.productId]);
        } else {
          this.router.navigate(['/products', item.productId]);
        }
      });
  }

  onPageChange(evt: PageEvent) {
    this.pageSize = evt.pageSize;
    this.pageNumber = evt.pageIndex + 1;
    this.loadData();
  }

  loadData() {
    this.loading = true;

    this.dateFrom.setHours(0, 0, 0, 0);
    this.dateTo.setHours(23, 59, 59, 999);

    const dateFrom = this.dateFrom;
    const dateTo = this.dateTo;

    const request = new ListProductImpressionsRequest();
    request.pageNumber = this.pageNumber;
    request.pageSize = this.pageSize;
    request.dateFrom = dateFrom;
    request.dateTo = dateTo;
    //request.manufacturer = this.manufacturer;
    request.manufacturerCompanyId = this.selectedCompany?.id;
    request.productName = this.product?.productName as ProductName;
    request.reportType = this.reportType;

    this.situAnalyticsClient.listProductImpressions(request)
      .then(response => {
        this.productImpressions = response.items ?? [];
        this.totalItems = response.totalItems ?? 0;

        this.loading = false;
      }, error => {
        this.loading = false;
      });
  }

  perProduct() {
    if (this.reportType === ProductImpressionReportType.PerProduct) {
      this.displayedColumns = this.defaultColumns;
      this.reportType = ProductImpressionReportType.Default;
    } else {
      this.displayedColumns = this.perProductColumns;
      this.reportType = ProductImpressionReportType.PerProduct;
    }
    this.pageNumber = 1;
    this.loadData();
  }

  perManufacturer() {
    if (this.reportType === ProductImpressionReportType.PerManufacturer) {
      this.displayedColumns = this.defaultColumns;
      this.reportType = ProductImpressionReportType.Default;
    } else {
      this.displayedColumns = this.perManufacturerColumns;
      this.reportType = ProductImpressionReportType.PerManufacturer;
    }
    this.pageNumber = 1;
    this.loadData();
  }

  buttonHighlight(reportButton: string): string {
    switch (reportButton) {
      case "perProduct":
        return this.reportType === ProductImpressionReportType.PerProduct ?
          "accent" : "basic";
      case "perManufacturer":
        return this.reportType === ProductImpressionReportType.PerManufacturer ?
          "accent" : "basic";
      default:
        return "basic";
    }
  }

  downloadCsv() {
    this.clearLastReport();

    this.dateFrom.setHours(0, 0, 0, 0);
    this.dateTo.setHours(23, 59, 59, 999);

    this.loading = true;

    this.situAnalyticsClient.createProductImpressionCsvReport(new CreateProductImpressionCsvReportRequest({
      ownerId: "",
      dateFrom: this.dateFrom,
      dateTo: this.dateTo,
      //manufacturer: this.manufacturer,
      manufacturerCompanyId: this.selectedCompany?.id,
      reportType: this.reportType
    }))
      .then(response => {
        this.reportId = response.reportId ?? '';
        this.pollForReportStatus();
      }, error => {
        this.loading = false;
      });
  }

  downloadDailyCsv() {
    this.clearLastReport();

    this.dateFrom.setHours(0, 0, 0, 0);
    this.dateTo.setHours(23, 59, 59, 999);

    const dateFrom = this.dateFrom;
    const dateTo = this.dateTo;

    this.loading = true;

    this.situAnalyticsClient.createDailyProductImpressionCsvReport(new CreateDailyProductImpressionCsvReportRequest({
      ownerId: "",
      dateFrom: dateFrom,
      dateTo: dateTo,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      reportType: ProductImpressionReportType.PerProduct
    }))
      .then(response => {
        this.reportId = response.reportId ?? '';
        this.pollForReportStatus();
      }, error => {
        this.loading = false;
      });
  }

  downloadManufacturerDetail(manufacturer: string) {
    this.clearLastReport();

    this.dateFrom.setHours(0, 0, 0, 0);
    this.dateTo.setHours(23, 59, 59, 999);

    const dateFrom = this.dateFrom;
    const dateTo = this.dateTo;

    this.loading = true;

    this.situAnalyticsClient.createProductImpressionCsvReport(new CreateProductImpressionCsvReportRequest({
      ownerId: "",
      dateFrom: dateFrom,
      dateTo: dateTo,
      //manufacturer: this.manufacturer,
      manufacturerCompanyId: this.selectedCompany?.id,
      reportType: ProductImpressionReportType.PerManufacturerDetail
    }))
      .then(response => {
        this.reportId = response.reportId ?? '';
        this.pollForReportStatus();
      }, error => {
        this.loading = false;
      });
  }

  showCsvButton(): boolean {
    return this.reportType === ProductImpressionReportType.PerManufacturer ||
      this.reportType === ProductImpressionReportType.PerProduct;
  }

  pollForReportStatus() {
    const pollDelayMilliseconds = 2000;
    const maxPolls = (60 * 60 * 1000) / pollDelayMilliseconds; // 1 hour max;

    this.pollForReportStatusSub = interval(pollDelayMilliseconds)
      .pipe(
        mergeMap(() => this.situAnalyticsClient.isReportReady({ id: this.reportId } as IsReportReadyRequest)
          .then((response) => {
            if (response.isReady === true) {
              this.isReportReady = true;
              this.loading = false;
              this.openDownloadReportDialog();
            }

            this.reportPollCount++;
            if (this.reportPollCount >= maxPolls) {
              // show timeout error
              this.loading = false;
            }
            return response;
          }, (e) => {
            if (this.reportErrorCount > 1) {
              this.loading = false;
              throw e;
            }

            this.reportErrorCount++;

            let response = new IsReportReadyResponse();
            response.isReady = false;
            return response;
          })),
        takeWhile(response => response.isReady === false && this.reportPollCount < maxPolls)
      )
      .subscribe();
  }

  openDownloadReportDialog() {
    this.dialog.open(DownloadReportDialogComponent, {
      data: {
        reportId: this.reportId
      }
    });
  }

  clearLastReport() {
    if (this.pollForReportStatusSub) {
      this.pollForReportStatusSub.unsubscribe();
    }

    this.isReportReady = false;
    this.reportId = '';
    this.reportErrorCount = 0;
    this.isReportReady = false;
  }
}
